import { create } from 'zustand';
import { dataService } from '@/services/data/data.service';
import { auditLogService } from '@/services/audit/audit-log.service';
import { useSessionTimeoutStore } from '@/stores/session-timeout.store';
import type { AuthUser } from '@/data/local-client.types';

/**
 * @fileoverview Local Authentication Store (Zustand)
 * @description
 * React/Zustand port of the Angular `LocalAuthService`. Handles all
 * authentication operations against the local-storage credential store: sign
 * up, sign in, sign out, password reset, email verification. The public method
 * surface is reproduced 1:1.
 *
 * Adaptation notes:
 * - The Angular `signOut()` performed Router navigation to `/login`. Per the
 *   React conversion rules, navigation happens in components, so the router
 *   call is removed here; `signOut()` just clears the session.
 * - The Angular `SessionTimeoutService` is wired here 1:1: `signIn` calls
 *   `startMonitoring()` and `signOut` calls `stopMonitoring()` via the
 *   `useSessionTimeoutStore` singleton (accessed with `getState()`).
 *
 * @dependencies
 * - dataService (local-client wrapper singleton)
 * - auditLogService
 */

export interface SignUpData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  username: string;
  title?: string;
  suffix?: string;
  institution_id?: string;
  custom_institution?: string;
  role?: string;
  specialty?: string;
  npi?: string;
  dea?: string;
  consent_accepted?: boolean;
}

export interface AuthResult {
  success: boolean;
  user?: AuthUser;
  error?: string;
}

interface AuthErrorLike {
  message?: string;
}

/**
 * Format auth errors into user-friendly messages (was private formatAuthError).
 */
function formatAuthError(error: AuthErrorLike): string {
  switch (error.message) {
    case 'Invalid login credentials':
      return 'Invalid email or password';
    case 'Email not confirmed':
      return 'Please verify your email address before signing in';
    case 'User already registered':
      return 'An account with this email already exists';
    default:
      return error.message || 'An authentication error occurred';
  }
}

/** Create user session record (was private createUserSession). */
async function createUserSession(userId: string, session: any): Promise<void> {
  try {
    await dataService.client.from('user_sessions').insert({
      user_id: userId,
      session_token: session?.access_token,
      ip_address: '',
      device_info: navigator.userAgent,
      expires_at: session?.expires_at ? new Date(session.expires_at).toISOString() : null,
    });
  } catch (error) {
    console.error('Session creation error:', error);
  }
}

/** Deactivate all user sessions (was private deactivateUserSessions). */
async function deactivateUserSessions(userId: string): Promise<void> {
  try {
    await dataService.client
      .from('user_sessions')
      .update({ is_active: false })
      .eq('user_id', userId)
      .eq('is_active', true);
  } catch (error) {
    console.error('Session deactivation error:', error);
  }
}

/** Track login attempt (was private trackLoginAttempt). */
async function trackLoginAttempt(
  email: string,
  isSuccessful: boolean,
  failureReason?: string,
): Promise<void> {
  try {
    await dataService.client.from('login_attempts').insert({
      email: email,
      ip_address: '',
      is_successful: isSuccessful,
      failure_reason: failureReason,
    });
  } catch (error) {
    console.error('Login attempt tracking error:', error);
  }
}

interface LocalAuthState {
  // Reactive state (was Angular signals)
  isLoading: boolean;
  authError: string | null;

  signUp: (signUpData: SignUpData) => Promise<AuthResult>;
  signIn: (credentials: { email: string; password: string }) => Promise<AuthResult>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<AuthResult>;
  updatePassword: (newPassword: string) => Promise<AuthResult>;
  getSession: () => Promise<any>;
  resendVerificationEmail: (email: string) => Promise<AuthResult>;
}

export const useLocalAuthStore = create<LocalAuthState>((set) => ({
  isLoading: false,
  authError: null,

  signUp: async (signUpData) => {
    try {
      set({ isLoading: true, authError: null });

      const { data, error } = await dataService.client.auth.signUp({
        email: signUpData.email,
        password: signUpData.password,
        options: {
          data: {
            first_name: signUpData.firstName,
            last_name: signUpData.lastName,
            username: signUpData.username,
            title: signUpData.title,
            suffix: signUpData.suffix,
            institution_id: signUpData.institution_id,
            custom_institution: signUpData.custom_institution,
            role: signUpData.role,
            specialty: signUpData.specialty,
            npi: signUpData.npi,
            dea: signUpData.dea,
            consent_accepted: signUpData.consent_accepted,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        set({ authError: formatAuthError(error) });
        return { success: false, error: formatAuthError(error) };
      }

      if (!data?.user) {
        set({ authError: 'User creation failed' });
        return { success: false, error: 'User creation failed' };
      }

      // Create the matching profile row (previously the DB trigger
      // `handle_new_user`; now performed in app code for local storage).
      await dataService.client.from('users').insert({
        id: data.user.id,
        email: signUpData.email,
        username: signUpData.username,
        first_name: signUpData.firstName,
        last_name: signUpData.lastName,
        title: signUpData.title,
        suffix: signUpData.suffix,
        institution_id: signUpData.institution_id,
        custom_institution: signUpData.custom_institution,
        role: signUpData.role ?? null,
        specialty: signUpData.specialty,
        npi: signUpData.npi,
        dea: signUpData.dea,
        consent_accepted: signUpData.consent_accepted,
        email_verified: true,
        account_status: 'active',
      });

      // Log successful registration
      await auditLogService.logCreate('users', data.user.id, {
        email: signUpData.email,
        role: signUpData.role,
      });

      return { success: true, user: data.user };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error during sign up';
      set({ authError: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      set({ isLoading: false });
    }
  },

  signIn: async (credentials) => {
    try {
      set({ isLoading: true, authError: null });

      const { data, error } = await dataService.client.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) {
        await trackLoginAttempt(credentials.email, false, error.message);
        await auditLogService.logAuthFailed(error.message ?? 'Sign in failed', {
          email: credentials.email,
        });
        set({ authError: formatAuthError(error) });
        return { success: false, error: formatAuthError(error) };
      }

      if (!data?.user) {
        await trackLoginAttempt(credentials.email, false, 'No user returned');
        await auditLogService.logAuthFailed('No user returned', { email: credentials.email });
        set({ authError: 'Sign in failed' });
        return { success: false, error: 'Sign in failed' };
      }

      // Track successful login
      await trackLoginAttempt(credentials.email, true);

      // Create/update session record
      await createUserSession(data.user.id, data.session);

      // Log successful login
      await auditLogService.logLogin({ email: credentials.email });

      // Start session timeout monitoring (30-minute inactivity timer).
      useSessionTimeoutStore.getState().startMonitoring();

      return { success: true, user: data.user };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error during sign in';
      set({ authError: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      set({ isLoading: false });
    }
  },

  signOut: async () => {
    try {
      set({ isLoading: true });

      // Stop session timeout monitoring.
      useSessionTimeoutStore.getState().stopMonitoring();

      // Mark session as inactive (fire and forget - don't wait)
      const userId = dataService.getCurrentUserId();
      if (userId) {
        deactivateUserSessions(userId).catch((err) => {
          console.warn('Session deactivation failed (non-critical):', err);
        });
      }

      // Log logout before signing out (fire and forget - don't wait)
      auditLogService.logLogout().catch((err) => {
        console.warn('Audit log failed (non-critical):', err);
      });

      const { error } = await dataService.client.auth.signOut();

      if (error) {
        set({ authError: formatAuthError(error) });
      }

      // NOTE: Angular navigated to '/login' here. In React, navigation is the
      // component's responsibility — the caller redirects after signOut resolves.
    } catch (error) {
      console.error('Unexpected sign out error:', error);
      // NOTE: Angular navigated to '/login' on error too — caller handles redirect.
    } finally {
      set({ isLoading: false });
    }
  },

  resetPassword: async (email) => {
    try {
      set({ isLoading: true, authError: null });

      const { error } = await dataService.client.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) {
        set({ authError: formatAuthError(error) });
        return { success: false, error: formatAuthError(error) };
      }

      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error during password reset';
      set({ authError: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      set({ isLoading: false });
    }
  },

  updatePassword: async (newPassword) => {
    try {
      set({ isLoading: true, authError: null });

      const { error } = await dataService.client.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        set({ authError: formatAuthError(error) });
        return { success: false, error: formatAuthError(error) };
      }

      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error during password update';
      set({ authError: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      set({ isLoading: false });
    }
  },

  getSession: async () => {
    try {
      const {
        data: { session },
      } = await dataService.client.auth.getSession();
      return session;
    } catch (error) {
      console.error('Error getting session:', error);
      return null;
    }
  },

  resendVerificationEmail: async (email) => {
    try {
      set({ isLoading: true, authError: null });

      const { error } = await dataService.client.auth.resend({
        type: 'signup',
        email: email,
      });

      if (error) {
        set({ authError: formatAuthError(error) });
        return { success: false, error: formatAuthError(error) };
      }

      return { success: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error resending verification email';
      set({ authError: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      set({ isLoading: false });
    }
  },
}));
